import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export const CommentsThread = ({ updateId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    if (!updateId) return;
    setLoading(true);
    try { const { data } = await api.get(`/updates/${updateId}/comments`); setComments(data); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [updateId]);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try { await api.post(`/updates/${updateId}/comments`, { body }); setBody(""); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not post"); }
    finally { setSending(false); }
  };

  const remove = async (id) => { try { await api.delete(`/comments/${id}`); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div data-testid="comments-thread">
      <div className="mb-3 flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Q&A / Comments</h3>
        <Badge variant="secondary" className="font-normal">{comments.length}</Badge>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No comments yet. Start the conversation.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="rounded-lg border bg-card p-3" data-testid={`comment-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{c.author_name}</span>
                    <Badge data-testid={`comment-role-${c.id}`} className="border-brand/30 bg-brand/10 text-brand-ink">{c.author_role}</Badge>
                  <span className="text-xs text-muted-foreground">{formatDateTime(c.created_at)}</span>
                </div>
                {c.author_user_id === user?.id && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-600" onClick={() => remove(c.id)} data-testid={`delete-comment-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                )}
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{c.body}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-start gap-2">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Ask a question or leave a note..." className="min-h-[60px]" data-testid="comment-input" />
        <Button onClick={submit} disabled={sending || !body.trim()} className="gap-1.5" data-testid="comment-submit">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};
